import "./MethodCompare.css";

const METHODS = ["Bastion scoring", "DCF", "Multiples", "Charts"] as const;

/** `fit` is how well the method does on the row, 1 (weak) to 3 (strong); rows without it are plain text. */
type Cell = { level?: string; fit?: 1 | 2 | 3; note: string };

const ROWS: { aspect: string; cells: [Cell, Cell, Cell, Cell] }[] = [
  {
    aspect: "Objectivity",
    cells: [
      { level: "High", fit: 3, note: "Same rules for every stock" },
      { level: "Low", fit: 1, note: "Depends on analyst assumptions" },
      { level: "Medium", fit: 2, note: "Depends on the peer group" },
      { level: "Low", fit: 1, note: "Open to interpretation" },
    ],
  },
  {
    aspect: "Sensitivity to assumptions",
    cells: [
      { level: "Low", fit: 3, note: "Many inputs, none decisive" },
      { level: "Very high", fit: 1, note: "One input can flip the result" },
      { level: "Medium", fit: 2, note: "Hinges on the chosen ratio" },
      { level: "High", fit: 1, note: "Hinges on where lines are drawn" },
    ],
  },
  {
    aspect: "Scalability",
    cells: [
      { level: "High", fit: 3, note: "Thousands of stocks at once" },
      { level: "Low", fit: 1, note: "Days of work per company" },
      { level: "Medium", fit: 2, note: "Screeners, but pass or fail" },
      { level: "High", fit: 3, note: "Fully automatable" },
    ],
  },
  {
    aspect: "Depth of analysis",
    cells: [
      { level: "High", fit: 3, note: "Many metrics, weighted" },
      { level: "High", fit: 2, note: "Deep, but for one company" },
      { level: "Low", fit: 1, note: "One or a few numbers" },
      { level: "Low", fit: 1, note: "Ignores fundamentals" },
    ],
  },
  {
    aspect: "Main weakness",
    cells: [
      { note: "Misses risks outside the data, such as lawsuits" },
      { note: "Built on subjective forecasts" },
      { note: "Shows one side of the business" },
      { note: "No link to the business" },
    ],
  },
  {
    aspect: "Best for",
    cells: [
      { note: "Finding ideas and building portfolios" },
      { note: "Deep dives into a single company" },
      { note: "Quick peer comparisons" },
      { note: "Timing short-term trades" },
    ],
  },
];

function Fit({ fit }: { fit: 1 | 2 | 3 }) {
  return (
    <span className="mcp-fit" aria-hidden>
      {[1, 2, 3].map((step) => (
        <span key={step} className={step <= fit ? "is-on" : undefined} />
      ))}
    </span>
  );
}

export default function MethodCompare() {
  return (
    <figure className="mcp">
      <figcaption id="mcp-caption" className="mcp-caption">
        Scoring vs. traditional valuation
      </figcaption>
      <div className="mcp-scroll">
        <table className="mcp-table" aria-labelledby="mcp-caption">
          <thead>
            <tr>
              <th scope="col" className="mcp-aspect">
                <span className="mcp-visually-hidden">Aspect</span>
              </th>
              {METHODS.map((method, index) => (
                <th key={method} scope="col" className={index === 0 ? "mcp-ours" : undefined}>
                  {method}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.aspect}>
                <th scope="row" className="mcp-aspect">
                  {row.aspect}
                </th>
                {row.cells.map((cell, index) => (
                  <td key={METHODS[index]} className={index === 0 ? "mcp-ours" : undefined}>
                    {cell.level ? (
                      <span className="mcp-level">
                        {cell.fit ? <Fit fit={cell.fit} /> : null}
                        {cell.level}
                      </span>
                    ) : null}
                    <span className="mcp-note">{cell.note}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
